import type { Config as ReminderConfig } from 'Automations/nodeDefinitions/definitions/reminder'
import { NodeType } from 'Automations/nodeDefinitions/types'
import {
  AutomationsPresentationV1Edge,
  AutomationsPresentationV1Node
} from '../../types'

export const REMINDER_NODE_TYPE: NodeType = 'REMINDER'

/**
 * The email address fields a downstream reminder inherits from the closest
 * upstream reminder (BADGE-5974). Only the addressing is copied — the subject,
 * body, schedule and approval settings are left untouched.
 */
export const REMINDER_RECIPIENT_FIELDS = ['to', 'cc', 'bcc'] as const

export type ReminderRecipients = Pick<
  ReminderConfig,
  (typeof REMINDER_RECIPIENT_FIELDS)[number]
>

/**
 * Find the recipient addresses of the reminder node closest upstream of
 * `nodeId`, or `null` if there is no upstream reminder. Walks edges upstream
 * (target→source) breadth-first, so the first reminder reached is the nearest by
 * edge distance. Cycle-safe.
 */
export const findClosestUpstreamReminderRecipients = ({
  nodeId,
  nodes,
  edges
}: {
  nodeId: string
  nodes: AutomationsPresentationV1Node[]
  edges: AutomationsPresentationV1Edge[]
}): ReminderRecipients | null => {
  const nodeById = new Map(nodes.map(node => [node.id, node]))

  const closest = findClosestUpstreamReminder({
    frontier: [nodeId],
    visited: new Set([nodeId]),
    edges,
    nodeById
  })
  if (!closest) return null

  return pickReminderRecipients(closest.data as ReminderConfig)
}

/**
 * Breadth-first step of the upstream walk: from the current `frontier`, look at
 * the not-yet-`visited` parent nodes (edges target→source); return the first
 * reminder among them, else recurse into the next level. Cycle-safe.
 */
const findClosestUpstreamReminder = ({
  frontier,
  visited,
  edges,
  nodeById
}: {
  frontier: string[]
  visited: Set<string>
  edges: AutomationsPresentationV1Edge[]
  nodeById: Map<string, AutomationsPresentationV1Node>
}): AutomationsPresentationV1Node | undefined => {
  const parentIds = Array.from(
    new Set(
      frontier.flatMap(id =>
        edges.filter(edge => edge.target === id).map(edge => edge.source)
      )
    )
  ).filter(id => !visited.has(id))

  if (parentIds.length === 0) return undefined

  const reminder = parentIds
    .map(id => nodeById.get(id))
    .find(node => node?.type === REMINDER_NODE_TYPE)

  if (reminder) return reminder

  return findClosestUpstreamReminder({
    frontier: parentIds,
    visited: new Set([...visited, ...parentIds]),
    edges,
    nodeById
  })
}

export const pickReminderRecipients = (
  config: Pick<ReminderConfig, (typeof REMINDER_RECIPIENT_FIELDS)[number]>
): ReminderRecipients => ({
  to: config.to,
  cc: config.cc,
  bcc: config.bcc
})
